/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';

const FADE_MS = 300;

// Fades the current view out then the next view in when `viewKey` changes.
const PageFade = ({ viewKey, children }) => {
  const [renderKey, setRenderKey] = useState(viewKey);
  const [visible, setVisible] = useState(true);
  const frozen = useRef(children);
  // While on the same page, keep the frozen snapshot current (no state update → no loop).
  if (viewKey === renderKey) frozen.current = children;

  // The swapped-in view only mounts once the fade completes (renderKey change),
  // so refresh the widget height then — not when viewKey first changes — or the
  // height would be measured against the outgoing view.
  useWidgetHeightRefresh([renderKey]);

  useEffect(() => {
    if (viewKey === renderKey) return undefined;
    setVisible(false); // fade the old view out
    const timer = setTimeout(() => {
      setRenderKey(viewKey); // swap to the new view
      setVisible(true); // fade the new view in
    }, FADE_MS);
    return () => clearTimeout(timer);
  }, [viewKey, renderKey]);

  const content = viewKey === renderKey ? children : frozen.current;
  return (
    <div
      css={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
      }}
      data-testid="pageFade"
    >
      {content}
    </div>
  );
};

export default PageFade;
